/**
 * FhbfPlatformSettingsCard — Admin controls for the FHBF Platform embed.
 *
 * Toggles per-view "enabled" and "open" (with a soft-close message),
 * theme, layout, brand colours, member portal URL, payments provider,
 * and directory visibility policy.
 *
 * All writes hit PUT /api/fhbf/onecom/admin/platform-settings and are
 * captured in the platform audit log.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Save, ShieldCheck, Loader2 } from 'lucide-react';
import { getToken } from '@/lib/authToken';

const API = process.env.REACT_APP_BACKEND_URL;
const authH = () => ({ Authorization: `Bearer ${getToken()}` });

const VIEWS = [
  { key: 'home', label: 'Home' },
  { key: 'membership', label: 'Membership' },
  { key: 'village', label: 'Business Village' },
  { key: 'directory', label: 'Directory' },
  { key: 'events', label: 'Events' },
  { key: 'sponsors', label: 'Sponsors' },
  { key: 'featured-media', label: 'Featured Media' },
];

export default function FhbfPlatformSettingsCard() {
  const [settings, setSettings] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/api/fhbf/onecom/admin/platform-settings`,
                                  { headers: authH() });
      setSettings(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load platform settings');
    }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!settings) return;
    setBusy(true);
    try {
      const payload = {
        brand: settings.brand,
        theme: settings.theme,
        layout: settings.layout,
        modules: settings.modules,
        auth: settings.auth,
        payments: settings.payments,
        directory: settings.directory,
      };
      const r = await axios.put(`${API}/api/fhbf/onecom/admin/platform-settings`,
                                  payload, { headers: authH() });
      setSettings(r.data);
      toast.success('Platform settings saved. Embed live-updates within 60 seconds.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally { setBusy(false); }
  };

  if (!settings) {
    return (
      <Card className="bg-slate-900/60 border-slate-800">
        <CardContent className="p-6 text-slate-400 text-sm">
          <Loader2 className="h-4 w-4 mr-2 inline-block animate-spin" /> Loading platform settings…
        </CardContent>
      </Card>
    );
  }

  const setField = (path, value) => {
    setSettings((prev) => {
      const next = structuredClone(prev);
      const parts = path.split('.');
      let ref = next;
      for (let i = 0; i < parts.length - 1; i++) {
        if (ref[parts[i]] === undefined || ref[parts[i]] === null) ref[parts[i]] = {};
        ref = ref[parts[i]];
      }
      ref[parts[parts.length - 1]] = value;
      return next;
    });
  };

  return (
    <Card className="bg-slate-900/60 border-emerald-500/30" data-testid="fhbf-platform-settings">
      <CardHeader className="flex flex-row items-start justify-between gap-4">
        <div>
          <CardTitle className="text-sm text-white flex items-center gap-2">
            <ShieldCheck className="h-4 w-4 text-emerald-300" /> Platform Embed — Controls
          </CardTitle>
          <CardDescription className="text-slate-400">
            Toggle each view, soft-close with a message, tune brand and payments.
            Every change is captured in the audit log.
          </CardDescription>
        </div>
        <Button onClick={save} disabled={busy} data-testid="platform-settings-save">
          <Save className="h-4 w-4 mr-1" /> {busy ? 'Saving…' : 'Save'}
        </Button>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Views */}
        <section className="space-y-2">
          <h3 className="text-xs uppercase tracking-wider text-slate-500">Views</h3>
          {VIEWS.map((v) => {
            const m = (settings.modules || {})[v.key] || { enabled: true, open: true, message: '' };
            return (
              <div key={v.key}
                   className="grid md:grid-cols-[160px_1fr_auto_auto] gap-3 items-center border border-slate-800 rounded p-3 bg-slate-800/20"
                   data-testid={`module-row-${v.key}`}>
                <div>
                  <div className="text-sm text-slate-200">{v.label}</div>
                  <div className="text-[10px] text-slate-500">/embed/fhbf?view={v.key}</div>
                </div>
                <Input
                  placeholder="Soft-close message (shown when 'Open' is off)"
                  value={m.message || ''}
                  onChange={(e) => setField(`modules.${v.key}.message`, e.target.value)}
                  className="bg-slate-900 border-slate-800 text-xs"
                  data-testid={`module-${v.key}-message`}
                />
                <label className="flex items-center gap-2 text-xs text-slate-400">
                  <Switch checked={!!m.enabled}
                           onCheckedChange={(val) => setField(`modules.${v.key}.enabled`, val)}
                           data-testid={`module-${v.key}-enabled`} />
                  Enabled
                </label>
                <label className="flex items-center gap-2 text-xs text-slate-400">
                  <Switch checked={!!m.open}
                           onCheckedChange={(val) => setField(`modules.${v.key}.open`, val)}
                           data-testid={`module-${v.key}-open`} />
                  Open
                </label>
              </div>
            );
          })}
        </section>

        {/* Brand & Theme */}
        <section>
          <h3 className="text-xs uppercase tracking-wider text-slate-500 mb-2">Brand &amp; Theme</h3>
          <div className="grid md:grid-cols-3 gap-3">
            <div>
              <Label className="text-xs text-slate-400">Theme (dark / light)</Label>
              <Input value={settings.theme || 'dark'}
                       onChange={(e) => setField('theme', e.target.value)}
                       className="bg-slate-900 border-slate-800 mt-1"
                       data-testid="platform-theme" />
            </div>
            <div>
              <Label className="text-xs text-slate-400">Primary colour</Label>
              <Input value={settings.brand?.primary_color || ''}
                       onChange={(e) => setField('brand.primary_color', e.target.value)}
                       placeholder="#0ea5e9"
                       className="bg-slate-900 border-slate-800 mt-1"
                       data-testid="platform-primary-color" />
            </div>
            <div>
              <Label className="text-xs text-slate-400">Accent colour</Label>
              <Input value={settings.brand?.accent_color || ''}
                       onChange={(e) => setField('brand.accent_color', e.target.value)}
                       placeholder="#f59e0b"
                       className="bg-slate-900 border-slate-800 mt-1"
                       data-testid="platform-accent-color" />
            </div>
          </div>
        </section>

        {/* Payments */}
        <section>
          <h3 className="text-xs uppercase tracking-wider text-slate-500 mb-2">Payments</h3>
          <div className="grid md:grid-cols-3 gap-3">
            <div>
              <Label className="text-xs text-slate-400">Provider</Label>
              <Input value={settings.payments?.provider || 'stripe'}
                       onChange={(e) => setField('payments.provider', e.target.value)}
                       className="bg-slate-900 border-slate-800 mt-1"
                       data-testid="platform-payments-provider" />
            </div>
            <div>
              <Label className="text-xs text-slate-400">Currency (ISO 4217)</Label>
              <Input value={settings.payments?.currency || 'usd'}
                       onChange={(e) => setField('payments.currency', e.target.value)}
                       className="bg-slate-900 border-slate-800 mt-1"
                       data-testid="platform-payments-currency" />
            </div>
            <div>
              <Label className="text-xs text-slate-400">Directory visibility</Label>
              <Input value={settings.directory?.visibility || 'approved_only'}
                       onChange={(e) => setField('directory.visibility', e.target.value)}
                       placeholder="approved_only | public_only"
                       className="bg-slate-900 border-slate-800 mt-1"
                       data-testid="platform-directory-visibility" />
            </div>
          </div>
        </section>

        {/* Auth */}
        <section>
          <h3 className="text-xs uppercase tracking-wider text-slate-500 mb-2">Member Portal (login redirect)</h3>
          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-slate-400">Portal path (same-origin)</Label>
              <Input value={settings.auth?.member_portal_url || ''}
                       onChange={(e) => setField('auth.member_portal_url', e.target.value)}
                       placeholder="/member-portal/login"
                       className="bg-slate-900 border-slate-800 mt-1"
                       data-testid="platform-portal-url" />
            </div>
            <div>
              <Label className="text-xs text-slate-400">Portal full URL (cross-origin override)</Label>
              <Input value={settings.auth?.member_portal_full || ''}
                       onChange={(e) => setField('auth.member_portal_full', e.target.value)}
                       placeholder="https://events.faithworldministries.com/member-portal/login"
                       className="bg-slate-900 border-slate-800 mt-1"
                       data-testid="platform-portal-full" />
            </div>
          </div>
        </section>
      </CardContent>
    </Card>
  );
}
